/**
 * 管理后台「运行与安全 A」页面的接口与类型：推理队列、请求日志（列表、看板、详情、导出）、生图记录、搜索记录。
 *
 * 背景：这几页对应 Alice 的 /inference-queue、/logs*、/vendor-conversations-export、/image-logs*、/image-cost-stats、
 *   /image-proxy、/stats/search-records，后端在 gateway_control 的 /control/admin/...（routes_admin_runtime.py）。
 * 设计意图：
 *   - 不改共用的 admin.ts：这里用自己的 createHttp（同一个刷新接口、同一套会话刷新与重试），路径逐字对应后端；
 *     会话彻底失效时再调一次 adminApi.me()，让共用层的「登录失效」监听器把应用带回登录页。
 *   - 导出接口返回的是文件流，不走 JSON 解析：这里用 fetch 取 Blob 再交给浏览器下载，响应头里的匹配条数
 *     与是否截断一并返回给页面提示。
 * 关键约束：金额字段一律是整数 unit（1e-8 美元）；null 表示成本未知，不能当 0；时间参数是带时区的 ISO 8601；
 *   错误统一抛 ApiError。
 */
import { adminApi } from './admin.ts'
import { ApiError, createHttp, type Query } from './http.ts'

const http = createHttp({
  refreshPath: '/admin/auth/refresh',
  noRefresh: ['/admin/auth/login', '/admin/auth/logout'],
  onAuthLost: () => {
    // me() 会走共用 http 层的刷新；刷新也失败时由它通知应用回到登录页。
    void adminApi.me().catch((e) => console.warn('[admin-runtime] 会话已失效', e))
  },
})

const C = '/control/admin'

// ------------------------------------------------------------------ 推理队列

export type QueueStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
export type PoolType = 'llm' | 'image'

export interface QueuePoolSummary {
  running: number
  queued: number
  max_concurrent: number
  channels: number
  oldest_wait_ms: number
  avg_wait_ms: number
  recent_requests: number
  recent_waited: number
}

export interface QueueItem {
  status: QueueStatus
  pool_type: PoolType
  job_type: string
  queued_at: string | null
  user_id: string
  nickname: string
  model_id: string
  current_wait_ms: number
  current_run_ms: number | null
  request_id: string
  /** 承接请求的渠道 ID（排队中的请求还没有渠道，为空）。 */
  worker_id: string
  error_message: string
  priority: string
}

export interface InferenceQueue {
  enabled: boolean
  summary: Record<PoolType, QueuePoolSummary>
  config: { priorities: { id: string; cap_percent: number; max_wait_s: number }[]; window_minutes: number }
  items: QueueItem[]
  total: number
  limit: number
  offset: number
}

// ------------------------------------------------------------------ 请求日志

export interface LogRecord {
  request_id: string
  created_at: string | null
  account_id: string
  account_name: string
  api_key_id: string
  end_user: string
  source: string
  purpose: string
  priority: string
  endpoint: string
  model: string
  shell_display_name: string
  channel_id: string
  upstream_model: string
  stream: boolean
  input_tokens: number
  cached_tokens: number
  cache_creation_tokens: number
  output_tokens: number
  images: number
  cost: number
  list_cost: number | null
  upstream_cost: number | null
  upstream_cost_basis: string
  discount_code: string
  billing_mode: string
  status: string
  error_code: string
  error_message: string
  status_code: number
  attempts: number
  ttft_ms: number | null
  duration_ms: number | null
  queue_wait_ms: number
  client_ip: string
  device_id: string
  app_version: string
  has_body: boolean
}

export interface LogSummary {
  requests: number
  success_requests: number
  failed_requests: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  cache_creation_tokens: number
  billed_requests: number
  user_cost: number
  known_cost_requests: number
  unknown_cost_requests: number
  known_upstream_cost: number
}

export interface LogList {
  logs: LogRecord[]
  total: number
  /** 没指定用户时为 null：不为了汇总去扫全表。 */
  summary: LogSummary | null
  total_size_gb: number
  limit: number
  offset: number
}

export interface LogBody {
  state: 'retained' | 'not_retained'
  request: unknown
  response: unknown
  expires_at: string | null
  retention_days: number
}

export interface LogDetail extends LogRecord {
  body: LogBody
}

export interface LogQuery {
  user_id?: string
  model?: string
  source?: string
  /** 带时区的 ISO 8601。 */
  start_time?: string
  end_time?: string
}

export interface DashMetrics {
  requests: number
  success_requests: number
  failed_requests: number
  users: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  billed_requests: number
  user_cost: number
  known_cost_requests: number
  unknown_cost_requests: number
  known_upstream_cost: number
  known_cost_user_cost: number
  avg_duration_ms: number | null
  p95_duration_ms: number | null
}

export type DashDimension = 'shell' | 'upstream'

export interface LogDashboard {
  window: { start: string; end: string; bucket: 'hour' | 'day'; start_defaulted: boolean; end_defaulted: boolean; tz_offset: number }
  dimension: DashDimension
  summary: DashMetrics
  trend: ({ bucket: string } & DashMetrics)[]
  model_trend: { key: string; label: string; requests: number[]; tokens: number[]; user_cost: number[] }[]
  models: ({ key: string; label: string } & DashMetrics)[]
  model_count: number
  sources: ({ source: string } & DashMetrics)[]
  users: ({ user_id: string } & DashMetrics)[]
}

export interface DownloadResult {
  /** 符合条件的总条数（响应头 X-Export-Matched）；接口没给就是 null。 */
  matched: number | null
  rows: number | null
  truncated: boolean
  filename: string
}

// ------------------------------------------------------------------ 生图记录

export interface ImageRecord {
  request_id: string
  created_at: string
  account_id: string
  account_name: string
  end_user: string
  model: string
  shell_display_name: string
  channel_id: string
  upstream_model: string
  status: 'success' | 'failed'
  status_code: number
  error_code: string
  error_message: string
  images: number
  cost: number
  list_cost: number | null
  upstream_cost: number | null
  upstream_cost_basis: string
  /** 用户实扣按图片数均分（与上游成本无关）；上游单张成本由 upstream_cost 除以 images 得到。 */
  cost_per_image: number | null
  duration_ms: number | null
  attempts: number
  queue_wait_ms: number
  source: string
  client_ip: string
  device_id: string
  app_version: string
  /** 只有正文仍在保存期内才有提示词。 */
  prompt: string
  has_body: boolean
  body_expires_at: string | null
  preview_count: number
}

export interface ImageLogs {
  total: number
  total_success: number | null
  total_failed: number | null
  records: ImageRecord[]
  limit: number
  offset: number
}

export interface ImageQuery {
  user_id?: string
  model?: string
  prompt?: string
  status?: 'all' | 'success' | 'failed'
  start_date?: string
  end_date?: string
}

export interface CostQuote {
  maintained: boolean
  enabled: boolean
  currency: string
  discount: string
  per_image: string | null
  prompt_per_1m: string | null
  completion_per_1m: string | null
}

export interface ImageCostDaily {
  date: string
  requests: number
  images: number
  known_cost_requests: number
  known_cost_images: number
  cost_coverage: number
  upstream_cost: number
  user_cost: number
  known_user_cost: number
  known_margin: number
  fallback_successes: number
  all_failed: number
}

export interface ImageCostProvider {
  channel_id: string
  model: string
  upstream_model: string
  requests: number
  images: number
  known_cost_requests: number
  known_cost_images: number
  cost_coverage: number
  upstream_cost: number
  user_cost: number
  known_user_cost: number
  known_margin: number
  avg_upstream_cost_per_image: number | null
  fallback_successes: number
  cost_quote: CostQuote | null
}

export interface ImageCostStats {
  start_date: string
  end_date: string
  summary: {
    requests: number
    images: number
    known_cost_images: number
    cost_coverage: number
    upstream_cost: number
    known_user_cost: number
    known_margin: number
    fallback_successes: number
    fallback_rate: number
    all_failed: number
  }
  daily: ImageCostDaily[]
  providers: ImageCostProvider[]
}

// ------------------------------------------------------------------ 搜索记录

export interface SearchRecords {
  /** 本仓网关没有联网搜索接口，恒为 false：页面据此显示空状态。 */
  supported: boolean
  records: unknown[]
  total: number
  total_failed: number
  total_fallback: number
  upstream_cost: number
  revenue: number
  limit: number
  offset: number
}

// ------------------------------------------------------------------ 下载

function filenameOf(resp: Response, fallback: string): string {
  const m = /filename="?([^";]+)"?/.exec(resp.headers.get('Content-Disposition') ?? '')
  return m?.[1] ?? fallback
}

function num(v: string | null): number | null {
  if (v === null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * 下载导出文件：取回 Blob 交给浏览器保存。
 * 背景：导出是流式 JSONL 或 gzip，不能走 JSON 解析的请求层；这里也要处理会话过期（401 先刷新再重试一次）。
 * 约束：失败时响应体是 {"error": {...}}，转成 ApiError 让页面按错误码翻译。
 */
async function download(path: string, query: Query, fallbackName: string): Promise<{ blob: Blob; info: DownloadResult }> {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    qs.set(k, String(v))
  }
  const url = `${path}?${qs}`
  for (let attempt = 0; attempt < 2; attempt++) {
    let resp: Response
    try {
      resp = await fetch(url, { credentials: 'same-origin' })
    } catch (e) {
      console.error('[admin-runtime] 下载网络错误', url, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    if (resp.status === 401 && attempt === 0) {
      try {
        await adminApi.me()
      } catch (e) {
        console.warn('[admin-runtime] 会话刷新失败，不再重试下载', e)
        throw new ApiError(401, 'not_logged_in', 'Login required.')
      }
      continue
    }
    if (!resp.ok) {
      let code = `http_${resp.status}`
      let message = resp.statusText
      try {
        const body = await resp.json()
        code = String(body?.error?.code ?? code)
        message = String(body?.error?.message ?? message)
      } catch {
        // 响应体不是 JSON，保留 http_<status>
      }
      console.warn('[admin-runtime] 下载失败', url, resp.status, code)
      throw new ApiError(resp.status, code, message)
    }
    const blob = await resp.blob()
    return {
      blob,
      info: {
        matched: num(resp.headers.get('X-Export-Matched')),
        rows: num(resp.headers.get('X-Export-Rows')),
        truncated: resp.headers.get('X-Export-Truncated') === 'true',
        filename: filenameOf(resp, fallbackName),
      },
    }
  }
  throw new ApiError(401, 'not_logged_in', 'Login required.')
}

/** 把 Blob 存成本地文件（创建临时链接点击后释放）。 */
export function saveBlob(blob: Blob, filename: string): void {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.setTimeout(() => {
    URL.revokeObjectURL(a.href)
    a.remove()
  }, 1000)
}

export const IMAGE_PROXY = `${C}/image-proxy`

/** 生图预览地址：同源、带 Cookie；后端只回传保留期内、通过文件头校验的图片。 */
export function imagePreviewUrl(requestId: string, index = 0): string {
  return `${IMAGE_PROXY}?request_id=${encodeURIComponent(requestId)}&index=${index}`
}

export const runtimeApi = {
  inferenceQueue: (q: { pool_type?: string; status?: string; limit?: number; offset?: number } = {}) =>
    http.get<InferenceQueue>(`${C}/inference-queue`, q as Query),

  logs: (q: LogQuery & { limit?: number; offset?: number }) => http.get<LogList>(`${C}/logs`, q as Query),
  logDashboard: (q: LogQuery & { dimension?: DashDimension; tz_offset?: number }) =>
    http.get<LogDashboard>(`${C}/logs/dashboard`, q as Query),
  logDetail: (requestId: string) => http.get<LogDetail>(`${C}/logs/${encodeURIComponent(requestId)}/detail`),
  exportLogs: (q: { user_id?: string; model?: string; source?: string; start_date?: string; end_date?: string; limit: number; with_bodies?: boolean }) =>
    download(`${C}/logs-export`, { ...q, format: 'jsonl' }, 'request-logs-export.jsonl'),
  exportVendor: (q: { start_date: string; end_date: string; limit: number; user_id?: string; model?: string }) =>
    download(`${C}/vendor-conversations-export`, q, 'vendor-conversations.jsonl.gz'),

  imageLogs: (q: ImageQuery & { limit?: number; offset?: number }) => http.get<ImageLogs>(`${C}/image-logs`, q as Query),
  exportImages: (q: ImageQuery & { limit?: number }) => download(`${C}/image-logs-export`, q as Query, 'image-logs-export.jsonl'),
  imageCostStats: (q: { start_date?: string; end_date?: string; tz_offset?: number } = {}) =>
    http.get<ImageCostStats>(`${C}/image-cost-stats`, q as Query),

  searchRecords: (q: { limit?: number; offset?: number } = {}) => http.get<SearchRecords>(`${C}/stats/search-records`, q as Query),
}
